import { defineStore } from "pinia";
import { computed } from "vue";
import { useAuth } from "@/composables/useAuth";
import { getRuntimeConfig, redirectUri } from "@/lib/runtime-config";
import { local, session } from "@/lib/storage";

export const useAuthStore = defineStore("auth", () => {
  const a = useAuth();

  const isAllowedDomain = computed(() => {
    if (!a.email.value) return false;
    const allowed = getRuntimeConfig().allowedEmailDomain.toLowerCase();
    return a.email.value.toLowerCase().endsWith(`@${allowed}`);
  });

  async function init(): Promise<void> {
    const url = new URL(window.location.href);
    // Cognito returns to the redirect URI (the bare origin). A code on any other path
    // is someone else's, e.g. GitHub's on /onboarding/github/created: never Cognito's.
    const code = url.pathname === new URL(redirectUri()).pathname ? url.searchParams.get("code") : null;
    const state = url.searchParams.get("state");
    const expected = session.get("oauth_state");

    if (code) {
      if (expected && state && expected !== state) {
        throw new Error("OAuth state mismatch — possible tampered redirect");
      }
      try {
        await a.exchangeCode(code);
      } finally {
        const clean = new URL(window.location.href);
        clean.search = "";
        history.replaceState({}, "", clean.toString());
      }
    } else if (local.get("refresh_token")) {
      try {
        await a.refresh();
      } catch (err) {
        console.warn("[auth.init] refresh failed, falling through signed-out", err);
      }
    }
  }

  return {
    // state — proxied refs so consumers can use `store.isSignedIn` reactively
    idToken: a.idToken,
    isSignedIn: a.isSignedIn,
    email: a.email,
    role: a.role,
    isAdmin: a.isAdmin,
    projects: a.projects,
    canSeeProject: a.canSeeProject,
    groups: a.groups,
    claims: a.claims,
    isAllowedDomain,
    // actions
    init,
    startSignIn: a.startSignIn,
    signOut: a.signOut,
    ensureFreshToken: a.ensureFreshToken,
  };
});
